import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  fetchAllOrders, 
  updateOrderStatus, 
  setSelectedOrder, 
  clearSelectedOrder 
} from "../../../redux/slices/adminOrderSlice";

const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Order Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Order Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Order Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Order ID:</span> #{order._id}</p>
                <p><span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {order.status}
                  </span>
                </p>
                <p><span className="font-medium">Total Price:</span> ₹{order.totalPrice?.toFixed(2)}</p>
                <p><span className="font-medium">Payment Status:</span> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    order.isPaid || order.paymentStatus === 'paid' || order.paymentStatus === 'completed' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {order.paymentStatus || (order.isPaid ? 'Paid' : 'Unpaid')}
                  </span>
                </p>
                <p><span className="font-medium">Payment Method:</span> {order.paymentMethod}</p>
                <p><span className="font-medium">Created At:</span> {new Date(order.createdAt).toLocaleString()}</p>
                {order.shippedAt && (
                  <p><span className="font-medium">Shipped At:</span> {new Date(order.shippedAt).toLocaleString()}</p>
                )}
                {order.deliveredAt && (
                  <p><span className="font-medium">Delivered At:</span> {new Date(order.deliveredAt).toLocaleString()}</p>
                )}
                {order.cancelledAt && (
                  <p><span className="font-medium">Cancelled At:</span> {new Date(order.cancelledAt).toLocaleString()}</p>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Customer Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Name:</span> {order.user?.name || 'N/A'}</p>
                <p><span className="font-medium">Email:</span> {order.user?.email || 'N/A'}</p>
                <p><span className="font-medium">Mobile:</span> {order.shippingAddress?.mobile || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Tracking Link (if shipped) */}
          {order.trackingLink && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
              <h3 className="font-semibold mb-2 text-blue-900">Tracking Information</h3>
              <p className="text-sm mb-2"><span className="font-medium">Tracking Number:</span> {order.trackingNumber || 'N/A'}</p>
              <a 
                href={order.trackingLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
              >
                Track Shipment →
              </a>
            </div>
          )}

          {/* Cancellation Reason (if cancelled) */}
          {order.cancellationReason && (
            <div className="bg-red-50 p-4 rounded-lg mb-6 border border-red-200">
              <h3 className="font-semibold mb-2 text-red-900">Cancellation Reason</h3>
              <p className="text-sm text-red-800">{order.cancellationReason}</p>
            </div>
          )}

          {/* Shipping Address */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-3">Shipping Address</h3>
            <div className="text-sm space-y-1">
              <p>{order.shippingAddress?.address}</p>
              <p>{order.shippingAddress?.city}, {order.shippingAddress?.state}</p>
              <p>{order.shippingAddress?.postalCode}, {order.shippingAddress?.country}</p>
              {order.shippingAddress?.mobile && <p>Contact: {order.shippingAddress.mobile}</p>}
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Order Items ({order.orderItems?.length || 0})</h3>
            <div className="space-y-3">
              {order.orderItems?.map((item, index) => (
                <div key={index} className="flex items-center space-x-4 bg-white p-3 rounded border">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium">{item.name}</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {item.size && <p>Size: {item.size}</p>}
                      {item.color && <p>Color: {item.color}</p>}
                      <p>Quantity: {item.quantity}</p>
                      <p>Unit Price: ₹{item.price?.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-lg">₹{(item.price * item.quantity)?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
              
              <div className="border-t pt-3 mt-4">
                <div className="flex justify-between items-center bg-white p-3 rounded font-semibold text-lg">
                  <span>Order Total:</span>
                  <span>₹{order.totalPrice?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { user } = useSelector((state) => state.auth);
  const { orders, selectedOrder, loading, error } = useSelector((state) => state.adminOrders);

  // State for inline forms
  const [editingOrder, setEditingOrder] = useState(null);
  const [trackingLink, setTrackingLink] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
    } else {
      dispatch(fetchAllOrders());
    }
  }, [dispatch, user, navigate]);

  const handleStatusChange = async (orderId, newStatus) => {
    const order = orders.find(o => o._id === orderId);
    
    // If changing to Shipped or Cancelled, show inline form
    if (newStatus === 'Shipped' || newStatus === 'Cancelled') {
      setEditingOrder(orderId);
      setSelectedStatus(newStatus);
      setTrackingLink('');
      setTrackingNumber('');
      setCancellationReason('');
    } else if (newStatus === 'Delivered') {
      // For Delivered, confirm before updating
      if (window.confirm('Mark this order as delivered? This will send a delivery confirmation email to the customer.')) {
        dispatch(updateOrderStatus({ id: orderId, status: newStatus }));
      }
    } else {
      // For Processing, update directly (no email sent)
      dispatch(updateOrderStatus({ id: orderId, status: newStatus }));
    }
  };

  const handleSubmitStatusChange = (orderId) => {
    const payload = { id: orderId, status: selectedStatus };

    if (selectedStatus === 'Shipped') {
      if (!trackingLink.trim()) {
        alert('Please enter a tracking link');
        return;
      }
      payload.trackingLink = trackingLink;
      payload.trackingNumber = trackingNumber;
    }

    if (selectedStatus === 'Cancelled') {
      if (!cancellationReason.trim()) {
        alert('Please enter a cancellation reason');
        return;
      }
      payload.cancellationReason = cancellationReason;
    }

    dispatch(updateOrderStatus(payload));
    setEditingOrder(null);
    setSelectedStatus('');
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setSelectedStatus('');
    setTrackingLink('');
    setTrackingNumber('');
    setCancellationReason('');
  };

  const handleOrderClick = (order) => {
    dispatch(setSelectedOrder(order));
  };

  const handleCloseModal = () => {
    dispatch(clearSelectedOrder());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing': return 'bg-yellow-100 text-yellow-800';
      case 'Shipped': return 'bg-blue-100 text-blue-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading orders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Order Management</h1>
      
      {orders && orders.length > 0 ? (
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <React.Fragment key={order._id}>
                  <tr className="hover:bg-gray-50">
                    <td 
                      className="px-6 py-4 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => handleOrderClick(order)}
                    >
                      #{order._id.slice(-8)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {order.user?.name || 'N/A'}
                      <div className="text-xs text-gray-500">{order.user?.email || ''}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{order.shippingAddress?.mobile || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-semibold">₹{order.totalPrice?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {order.isPaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {editingOrder === order._id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSubmitStatusChange(order._id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                          >
                            ✓ Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-400 text-white px-3 py-1 rounded text-xs hover:bg-gray-500"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      ) : (
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order._id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Processing">Processing</option>
                          <option value="Shipped">Shipped</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      )}
                    </td>
                  </tr>
                  
                  {/* Inline Form Row */}
                  {editingOrder === order._id && (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 bg-blue-50 border-t-2 border-blue-200">
                        {selectedStatus === 'Shipped' && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-blue-900">📦 Shipping Details</h4>
                            <div>
                              <label className="block text-sm font-medium mb-1">Tracking Link *</label>
                              <input
                                type="url"
                                value={trackingLink}
                                onChange={(e) => setTrackingLink(e.target.value)}
                                placeholder="https://courierupdates.com/?awb=76730032245"
                                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Tracking Number (Optional)</label>
                              <input
                                type="text"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="76730032245"
                                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        )}
                        
                        {selectedStatus === 'Cancelled' && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-red-900">✕ Cancellation Details</h4>
                            <div>
                              <label className="block text-sm font-medium mb-1">Reason for Cancellation *</label>
                              <textarea
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="Enter the reason for cancellation (e.g., Customer requested cancellation, Out of stock, Payment not received, etc.)"
                                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                                rows="3"
                                required
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 text-lg">No orders found.</div>
      )}

      {selectedOrder && <OrderDetailsModal order={selectedOrder} onClose={handleCloseModal} />}
    </div>
  );
};

export default OrderManagement;
